import type {
  UserEmaIndicator,
  UserIndicator,
  UserIndicatorConfig,
  UserOrbIndicator,
  UserPrevDayHighLowIndicator,
  UserPremarketHighLowIndicator,
  UserSmaIndicator,
  UserVolumeIndicator,
  UserVwapIndicator,
} from './types'
import { intervalToBucket } from './types'
import type { BarInterval } from '../../api/marketData'

// ── MA sets keyed by bucket ──────────────────────────────────────────────────

const MA_SETS: Record<string, { kind: 'ema' | 'sma'; period: number }[]> = {
  intraday: [
    { kind: 'ema', period: 9 },
    { kind: 'ema', period: 34 },
    { kind: 'ema', period: 89 },
  ],
  hourly: [
    { kind: 'ema', period: 20 },
    { kind: 'ema', period: 50 },
    { kind: 'ema', period: 200 },
  ],
  daily: [
    { kind: 'ema', period: 9 },
    { kind: 'ema', period: 21 },
    { kind: 'ema', period: 50 },
    { kind: 'sma', period: 200 },
  ],
  weekly: [
    { kind: 'ema', period: 10 },
    { kind: 'ema', period: 20 },
    { kind: 'ema', period: 50 },
    { kind: 'ema', period: 200 },
  ],
}

// ── Palette of distinct colors for individual indicators ─────────────────────

const INDICATOR_PALETTE = [
  '#10b981', // green
  '#3b82f6', // blue
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#14b8a6', // teal
  '#6366f1', // indigo
]

/** Pick a distinct color from the palette by index. */
function paletteColor(index: number): string {
  return INDICATOR_PALETTE[index % INDICATOR_PALETTE.length]
}

// ── Default indicator colors (shared / structural) ─────────────────────────────

export const DEFAULT_INDICATOR_COLORS = {
  ema: '#10b981',
  sma: '#3b82f6',
  vwap: '#f59e0b',
  priceLevel: '#a1a1aa',
  premarketHighLow: '#6366f1',
  prevDayHighLow: '#fb923c',
  orb: '#ec4899',
  volumeUp: '#10b981',
  volumeDown: '#ef4444',
  fillBull: 'rgba(16,185,129,0.35)',
  fillBear: 'rgba(239,68,68,0.35)',
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function makeEmaIndicator(kind: 'ema' | 'sma', period: number, bucket: string, colorIndex: number): UserEmaIndicator | UserSmaIndicator {
  const base: UserEmaIndicator | UserSmaIndicator = {
    id: `${kind}-${period}-${bucket}`,
    type: kind,
    enabled: true,
    period,
    color: paletteColor(colorIndex),
    width: 1,
    style: 'solid',
  }
  return base
}

function makeVwapIndicator(bucket: string, colorIndex: number): UserVwapIndicator {
  return {
    id: `vwap-${bucket}`,
    type: 'vwap',
    enabled: true,
    source: 'hlc3',
    color: paletteColor(colorIndex),
    width: 1,
  }
}

function makePremarketIndicator(): UserPremarketHighLowIndicator {
  return {
    id: 'premarket-high-low',
    type: 'premarketHighLow',
    enabled: true,
    highColor: DEFAULT_INDICATOR_COLORS.premarketHighLow,
    lowColor: DEFAULT_INDICATOR_COLORS.premarketHighLow,
  }
}
function makePrevDayIndicator(): UserPrevDayHighLowIndicator {
  return {
    id: 'prevday-high-low',
    type: 'prevDayHighLow',
    enabled: true,
    highColor: DEFAULT_INDICATOR_COLORS.prevDayHighLow,
    lowColor: DEFAULT_INDICATOR_COLORS.prevDayHighLow,
  }
}

function makeOrbIndicator(): UserOrbIndicator {
  return {
    id: 'orb-high-low',
    type: 'orb',
    enabled: true,
    highColor: DEFAULT_INDICATOR_COLORS.orb,
    lowColor: DEFAULT_INDICATOR_COLORS.orb,
  }
}

function makeVolumeIndicator(): UserVolumeIndicator {
  return {
    id: 'volume',
    type: 'volume',
    enabled: true,
    upColor: DEFAULT_INDICATOR_COLORS.volumeUp,
    downColor: DEFAULT_INDICATOR_COLORS.volumeDown,
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Build a default `UserIndicatorConfig` for the given chart interval.
 * Pure function — always returns a fresh object graph.
 */
export function getDefaultConfig(interval: BarInterval): UserIndicatorConfig {
  const bucket = intervalToBucket(interval)
  const maSpecs = MA_SETS[bucket]
  const indicators: UserIndicator[] = []

  // Moving averages from the bucket's MA set — each gets a unique color
  for (let i = 0; i < maSpecs.length; i++) {
    const spec = maSpecs[i]
    indicators.push(makeEmaIndicator(spec.kind, spec.period, bucket, i))
  }

  // Intraday + hourly get VWAP, premarket, prev-day overlays
  if (bucket === 'intraday' || bucket === 'hourly') {
    const vwapIdx = maSpecs.length
    indicators.push(makeVwapIndicator(bucket, vwapIdx))
    indicators.push(makePremarketIndicator())
    indicators.push(makePrevDayIndicator())
    indicators.push(makeOrbIndicator())
  }

  // Volume applies to every interval (overlay at the bottom of the price pane).
  indicators.push(makeVolumeIndicator())

  return {
    version: 2,
    indicators,
  }
}
